import React from 'react'
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import WhatsAppButton from './components/common/WhatsAppButton'
import CookieConsent from './components/common/CookieConsent'
import ScrollToTop from './components/common/ScrollToTop'
import PageLoader from './components/common/PageLoader'
import DebugTrigger from './components/debug/DebugTrigger'
import { AuthProvider } from './contexts/AuthContext'
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { useSessionManager } from './hooks/useSessionManager'
import TrialExpiredModal from './components/billing/TrialExpiredModal'
import { useTrialStatus } from './hooks/useTrialStatus'
// DESATIVADO: import { ServiceWorkerNotifications } from './components/common/ServiceWorkerNotifications'
// DESATIVADO: import { PerformanceMonitor } from './components/common/PerformanceMonitor'
import ProtectedRoute from './components/auth/ProtectedRoute'
import TrialGuard from './components/auth/TrialGuard'
import AdminProtectedRoute from './components/auth/AdminProtectedRoute'
import ErrorBoundary from './components/common/ErrorBoundary'
import MainLayout from './components/layout/MainLayout'
import AdminLayout from './components/admin/AdminLayout'
import LandingPage from './components/landing/LandingPage'
import AuthPage from './components/auth/AuthPage'
import SchoolMemberRoute from './components/auth/SchoolMemberRoute'
import * as ReactHelmetAsync from 'react-helmet-async'
const { HelmetProvider } = ReactHelmetAsync
import { QuestionSelectionProvider } from './contexts/QuestionSelectionContext'
import { useFontPreload } from './hooks/useFontPreload'
import { useReferralCapture } from './hooks/useReferralCapture'
import { useAttributionCapture } from './hooks/useAttributionCapture'

// Lazy-loaded route components - carregados sob demanda para reduzir bundle inicial
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'))
const QuestionBank = React.lazy(() => import('./components/questions/QuestionBank'))
// Roteia desktop/mobile por viewport (ResponsiveAssessmentEditor decide)
const AssessmentEditor = React.lazy(() => import('./components/editor/ResponsiveAssessmentEditor'))
const AssessmentWizard = React.lazy(() => import('./components/wizard/AssessmentWizard'))
const MyAssessments = React.lazy(() => import('./components/assessments/MyAssessments'))
const Templates = React.lazy(() => import('./components/templates/Templates'))
const AssessmentBank = React.lazy(() => import('./components/assessmentBank/AssessmentBank'))
const Analytics = React.lazy(() => import('./components/analytics/Analytics'))
const Settings = React.lazy(() => import('./components/settings/Settings'))
const Billing = React.lazy(() => import('./components/billing/Billing'))
const ReferralPage = React.lazy(() => import('./components/billing/ReferralPage'))
const ReferralRedirect = React.lazy(() => import('./components/billing/ReferralRedirect'))
const SubscriptionManagement = React.lazy(() => import('./components/subscription/SubscriptionManagement'))
const Collaboration = React.lazy(() => import('./components/collaboration/Collaboration'))
const Reports = React.lazy(() => import('./components/reports/Reports'))
const HelpCenter = React.lazy(() => import('./components/help/HelpCenter'))
const ResetPasswordForm = React.lazy(() => import('./components/auth/ResetPasswordForm'))
const ConfirmEmailPage = React.lazy(() => import('./components/auth/ConfirmEmailPage'))
const AcceptInvitePage = React.lazy(() => import('./components/auth/AcceptInvitePage'))
const SchoolManagement = React.lazy(() => import('./components/admin/SchoolManagement'))
const SchoolArea = React.lazy(() => import('./components/school/SchoolArea'))
const PaymentConfirmedPage = React.lazy(() => import('./components/school/PaymentConfirmedPage'))

// Public pages - lazy loaded
const PublicAssessmentList = React.lazy(() => import('./components/public/PublicAssessmentList'))
const PublicAssessmentDetail = React.lazy(() => import('./components/public/PublicAssessmentDetail'))
const PublicCategoryPage = React.lazy(() => import('./components/public/PublicCategoryPage'))
const AboutPage = React.lazy(() => import('./components/public/AboutPage'))
const ContactPage = React.lazy(() => import('./components/public/ContactPage'))
const CustomPlanRequestPage = React.lazy(() => import('./components/public/CustomPlanRequestPage'))
const TermsPage = React.lazy(() => import('./components/public/TermsPage'))
const PrivacyPage = React.lazy(() => import('./components/public/PrivacyPage'))
const CookiesPage = React.lazy(() => import('./components/public/CookiesPage'))
const LGPDPage = React.lazy(() => import('./components/public/LGPDPage'))
const AjudaPage = React.lazy(() => import('./components/public/AjudaPage'))
const UnsubscribePage = React.lazy(() => import('./components/public/UnsubscribePage'))
const LandingProfessor = React.lazy(() => import('./components/landing/LandingProfessor'))
const LandingEscola = React.lazy(() => import('./components/landing/LandingEscola'))
const TokensShowcase = React.lazy(() => import('./components/common/TokensShowcase'))
const NotFoundPage = React.lazy(() => import('./components/public/NotFoundPage'))
const ServerErrorPage = React.lazy(() => import('./components/public/ServerErrorPage'))
const SeasonalCampaignLanding = React.lazy(() => import('./components/seasonal/SeasonalCampaignLanding'))

// Admin pages - lazy loaded
const AdminOverview = React.lazy(() => import('./components/admin/AdminOverview'))
const UserManagement = React.lazy(() => import('./components/admin/UserManagement'))
const QuestionManagement = React.lazy(() => import('./components/admin/QuestionManagement'))
const UnifiedFeedbackManagement = React.lazy(() => import('./components/admin/UnifiedFeedbackManagement'))
const BulkQuestionGenerator = React.lazy(() => import('./components/admin/BulkQuestionGenerator'))
const BulkQuestionReview = React.lazy(() => import('./components/admin/BulkQuestionReview'))
const BulkGenerationJobManager = React.lazy(() => import('./components/admin/BulkGenerationJobManager'))
const GenerationBatchManager = React.lazy(() => import('./components/admin/GenerationBatchManager'))
const AssessmentManagement = React.lazy(() => import('./components/admin/AssessmentManagement'))
const PublicAssessmentManagement = React.lazy(() => import('./components/admin/PublicAssessmentManagement'))
const TemplateManagement = React.lazy(() => import('./components/admin/TemplateManagement'))
const SubscriptionOverview = React.lazy(() => import('./components/admin/SubscriptionOverview'))
const AdminAnalytics = React.lazy(() => import('./components/admin/AdminAnalytics'))
const SystemSettings = React.lazy(() => import('./components/admin/SystemSettings'))
const SEOManagement = React.lazy(() => import('./components/admin/SEOManagement'))
const NotificationManagement = React.lazy(() => import('./components/admin/NotificationManagement'))
const PlanManagement = React.lazy(() => import('./components/admin/PlanManagement'))
const AIProviderSettings = React.lazy(() => import('./components/admin/AIProviderSettings'))
const TrialManagement = React.lazy(() => import('./components/admin/TrialManagement'))
const SeasonalCampaignManagement = React.lazy(() => import('./components/admin/SeasonalCampaignManagement'))
const SeasonalCampaignForm = React.lazy(() => import('./components/admin/SeasonalCampaignForm'))
const SeasonalCampaignAnalytics = React.lazy(() => import('./components/admin/SeasonalCampaignAnalytics'))
const SeasonalLeadManagement = React.lazy(() => import('./components/admin/SeasonalLeadManagement'))
const CustomPlanRequestsManagement = React.lazy(() => import('./components/admin/CustomPlanRequestsManagement'))
const SecurityDashboard = React.lazy(() => import('./components/admin/SecurityDashboard'))
const AutoGenerationManager = React.lazy(() => import('./components/admin/AutoGenerationManager'))

// Blog routes - lazy loaded
const BlogDashboard = React.lazy(() => import('./components/blog/admin/BlogDashboard'))
const BlogPostEditor = React.lazy(() => import('./components/blog/admin/BlogPostEditor'))
const BlogCategoryManager = React.lazy(() => import('./components/blog/admin/BlogCategoryManager'))
const BlogTagManager = React.lazy(() => import('./components/blog/admin/BlogTagManager'))
const NewsletterManagement = React.lazy(() => import('./components/blog/admin/NewsletterManagement'))
const BlogAutoGeneration = React.lazy(() => import('./components/blog/admin/BlogAutoGeneration'))
const BlogContentCalendar = React.lazy(() => import('./components/blog/admin/BlogContentCalendar'))
const BlogCommemorativeDates = React.lazy(() => import('./components/blog/admin/BlogCommemorativeDates'))
const BlogListPage = React.lazy(() => import('./components/blog/public/BlogListPage'))
const BlogPostPage = React.lazy(() => import('./components/blog/public/BlogPostPage'))
const BlogCategoryPage = React.lazy(() => import('./components/blog/public/BlogCategoryPage'))
const BlogTagPage = React.lazy(() => import('./components/blog/public/BlogTagPage'))
const BlogPostPreviewPage = React.lazy(() => import('./components/blog/public/BlogPostPreviewPage'))
const BlogSeriesPage = React.lazy(() => import('./components/blog/public/BlogSeriesPage'))

// Fallback de loading para componentes lazy-loaded (Caderno DS)
const LazyFallback = () => <PageLoader />

const LazyFallbackInline = () => <PageLoader variant="inline" />

// DESATIVADO: Create intelligent query client
// import { createIntelligentQueryClient } from './lib/cache/cacheConfig'

// const queryClient = createIntelligentQueryClient()

// QueryClient otimizado para reconexão, gerenciamento de sessão e timeouts
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos

      // Timeout configuration
      queryFn: undefined, // Will be overridden by individual queries

      retry: (failureCount, error: any) => {
        // Não retry para erros 4xx (client errors) ou timeouts
        if (error?.status >= 400 && error?.status < 500) {
          return false
        }
        // Não retry para timeouts
        if (error?.message?.includes('timeout') || error?.message?.includes('aborted')) {
          return false
        }
        // Retry até 2 vezes para outros erros (reduzido de 3 para 2)
        return failureCount < 2
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 15000), // Max 15s delay

      // Configurações otimizadas para reconexão
      networkMode: 'online',
      refetchOnReconnect: 'always', // Sempre refetch na reconexão
      refetchOnWindowFocus: false, // Desabilitado - será gerenciado pelo SessionManager

      // Configurações para melhor UX
      refetchOnMount: true,
      refetchInterval: false, // Sem polling automático

      // Add meta for timeout handling
      meta: {
        timeout: 12000 // Default 12 second timeout for queries
      }
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Não retry para timeouts ou client errors
        if (error?.message?.includes('timeout') ||
            error?.message?.includes('aborted') ||
            (error?.status >= 400 && error?.status < 500)) {
          return false
        }
        return failureCount < 1
      },
      networkMode: 'online',
      meta: {
        timeout: 15000 // Default 15 second timeout for mutations
      }
    },
  },
})

// Componente interno para gerenciar sessão
const SessionManagerWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 🚀 PRELOAD DE FONTES: Iniciar em background (2s após app carregar)
  // Reduz tempo de primeira geração de PDF de 2-5s para ~500ms
  useFontPreload({
    enabled: true,  // Habilitar preload
    delay: 2000     // Aguardar 2s para não bloquear inicialização
  })

  // Captura ?ref= em qualquer URL pública e armazena para o signup
  useReferralCapture()

  // Captura UTM/click ids/referrer da PRIMEIRA visita. Tem que viver aqui, e não
  // no RegisterForm: numa SPA a query string se perde na navegação de rota.
  useAttributionCapture()

  // Configurar o session manager com debug habilitado em desenvolvimento
  useSessionManager({
    debug: process.env.NODE_ENV === 'development',
    staleSessionThreshold: 30 * 60 * 1000, // 30 minutos
    staleDataThreshold: 5 * 60 * 1000, // 5 minutos
    criticalQueries: [
      'questions',
      'adminStats',
      'systemSettings',
      'userProfile',
      'subscriptions',
      'assessments',
      'schools',
      'notifications'
    ]
  })

  return <>{children}</>
}

// Rotas onde o usuário expirado precisa agir (assinar, gerenciar conta) — sem modal bloqueante
const TRIAL_MODAL_EXEMPT_PATHS = [
  '/app/billing',
  '/app/indicar',
  '/app/settings',
  '/app/subscription-management',
  '/app/help',
  '/app/escola',
]

// Componente para gerenciar trial banners e modals - APENAS para usuários no dashboard
const TrialComponents: React.FC = () => {
  const { loading: subscriptionLoading } = useSubscription()
  const { hasExpired } = useTrialStatus()
  const location = useLocation()

  // Don't render trial UI while subscription data is still loading.
  // Prevents false hasExpired during the initialization race condition.
  if (subscriptionLoading) return null

  const isExemptPath = TRIAL_MODAL_EXEMPT_PATHS.some(path => location.pathname.startsWith(path))

  return (
    <>
      {/* Modal bloqueante quando trial expira (exceto onde o usuário precisa agir) */}
      {hasExpired && !isExemptPath && <TrialExpiredModal isOpen={true} />}

      {/* Banner de countdown movido para dentro do MainLayout (coluna de 100vh):
          aqui fora ele somava 86px ao documento, criando scrollbar externa e
          colisão com o Header (ambos sticky top-0 z-40). */}
    </>
  )
}

// Wrapper para rotas do dashboard do usuário com componentes de trial
const UserDashboardWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <>
      <TrialComponents />
      {children}
    </>
  )
}

/**
 * Tudo que vivia dentro de <BrowserRouter>: providers + árvores <Routes>
 * descendentes. É o elemento da única rota do data router (ver `router`).
 */
const AppShell: React.FC = () => (
          <QueryClientProvider client={queryClient}>
            <SessionManagerWrapper>
              <AuthProvider>
                <SubscriptionProvider>
                  <ThemeProvider>
                    <QuestionSelectionProvider>
                    <div className="min-h-screen bg-cream-50 dark:bg-ink-900">
                      <Toaster
                        position="top-right"
                        toastOptions={{
                          duration: 4000,
                          // react-hot-toast exige style inline — usar var() dos
                          // tokens Caderno em vez de hex (regra do DS)
                          style: {
                            background: 'var(--color-ink-900)',
                            color: 'var(--color-cream-50)',
                          },
                          success: {
                            duration: 3000,
                            iconTheme: {
                              primary: 'var(--color-success)',
                              secondary: 'var(--color-cream-50)',
                            },
                          },
                          error: {
                            duration: 4000,
                            iconTheme: {
                              primary: 'var(--color-danger)',
                              secondary: 'var(--color-cream-50)',
                            },
                          },
                        }}
                      />

                      <ScrollToTop />
                      <WhatsAppButton />
                      <CookieConsent />

                      {/* DESATIVADO: Performance and Service Worker components */}
                      {/* <ServiceWorkerNotifications /> */}
                      {/* <PerformanceMonitor /> */}

                      <Routes>
                        {/* Caderno design tokens showcase — dev only */}
                        {import.meta.env.DEV && (
                          <Route path="/design-tokens" element={<React.Suspense fallback={<LazyFallback />}><TokensShowcase /></React.Suspense>} />
                        )}

                        {/* Landing Page */}
                        <Route path="/" element={<LandingPage />} />

                        {/* Landing variants para tráfego pago */}
                        <Route path="/lp/professor" element={<React.Suspense fallback={<LazyFallback />}><LandingProfessor /></React.Suspense>} />
                        <Route path="/lp/escola" element={<React.Suspense fallback={<LazyFallback />}><LandingEscola /></React.Suspense>} />

                        {/* Public Assessment Routes */}
                        <Route path="/avaliacoes" element={<React.Suspense fallback={<LazyFallback />}><PublicAssessmentList /></React.Suspense>} />
                        <Route path="/avaliacoes/:slug" element={<React.Suspense fallback={<LazyFallback />}><PublicAssessmentDetail /></React.Suspense>} />
                        <Route path="/avaliacoes/categoria/:categorySlug" element={<React.Suspense fallback={<LazyFallback />}><PublicCategoryPage /></React.Suspense>} />

                        {/* Blog Public Routes */}
                        <Route path="/blog" element={<React.Suspense fallback={<LazyFallback />}><BlogListPage /></React.Suspense>} />
                        <Route path="/blog/:slug" element={<React.Suspense fallback={<LazyFallback />}><BlogPostPage /></React.Suspense>} />
                        <Route path="/blog/categoria/:slug" element={<React.Suspense fallback={<LazyFallback />}><BlogCategoryPage /></React.Suspense>} />
                        <Route path="/blog/tag/:slug" element={<React.Suspense fallback={<LazyFallback />}><BlogTagPage /></React.Suspense>} />
                        <Route path="/blog/preview/:token" element={<React.Suspense fallback={<LazyFallback />}><BlogPostPreviewPage /></React.Suspense>} />
                        <Route path="/blog/serie/:slug" element={<React.Suspense fallback={<LazyFallback />}><BlogSeriesPage /></React.Suspense>} />

                        {/* Seasonal Campaign Routes */}
                        <Route path="/campanha/:slug" element={<React.Suspense fallback={<LazyFallback />}><SeasonalCampaignLanding /></React.Suspense>} />

                        {/* Public Pages */}
                        <Route path="/about" element={<React.Suspense fallback={<LazyFallback />}><AboutPage /></React.Suspense>} />
                        <Route path="/contact" element={<React.Suspense fallback={<LazyFallback />}><ContactPage /></React.Suspense>} />
                        <Route path="/planos/personalizado" element={<React.Suspense fallback={<LazyFallback />}><CustomPlanRequestPage /></React.Suspense>} />
                        <Route path="/escola/pagamento-confirmado" element={<React.Suspense fallback={<LazyFallback />}><PaymentConfirmedPage /></React.Suspense>} />

                        {/* Static Pages */}
                        <Route path="/terms" element={<React.Suspense fallback={<LazyFallback />}><TermsPage /></React.Suspense>} />
                        <Route path="/privacy" element={<React.Suspense fallback={<LazyFallback />}><PrivacyPage /></React.Suspense>} />
                        <Route path="/cookies" element={<React.Suspense fallback={<LazyFallback />}><CookiesPage /></React.Suspense>} />
                        <Route path="/lgpd" element={<React.Suspense fallback={<LazyFallback />}><LGPDPage /></React.Suspense>} />
                        <Route path="/ajuda" element={<React.Suspense fallback={<LazyFallback />}><AjudaPage /></React.Suspense>} />
                        <Route path="/indicar" element={<React.Suspense fallback={<LazyFallback />}><ReferralRedirect /></React.Suspense>} />
                        <Route path="/descadastrar" element={<React.Suspense fallback={<LazyFallback />}><UnsubscribePage /></React.Suspense>} />

                        {/* Error Pages */}
                        <Route path="/404" element={<React.Suspense fallback={<LazyFallback />}><NotFoundPage /></React.Suspense>} />
                        <Route path="/500" element={<React.Suspense fallback={<LazyFallback />}><ServerErrorPage /></React.Suspense>} />

                        {/* Auth Pages */}
                        <Route path="/login" element={<AuthPage />} />
                        <Route path="/register" element={<AuthPage />} />
                        <Route path="/forgot-password" element={<AuthPage />} />
                        <Route path="/reset-password" element={
                          <React.Suspense fallback={<LazyFallback />}>
                            <div className="min-h-screen bg-gradient-to-br from-terra-50 via-surface to-terra-50 dark:from-ink-900 dark:via-ink-700 dark:to-ink-900 flex items-center justify-center p-4">
                              <div className="bg-surface dark:bg-ink-700 rounded-lg shadow-caderno-lg p-8 lg:p-10 w-full max-w-md">
                                <ResetPasswordForm />
                              </div>
                            </div>
                          </React.Suspense>
                        } />
                        {/* Destino dos links dos e-mails de auth ({{ .SiteURL }}/auth/confirm) —
                            troca o token_hash por sessão via verifyOtp sem expor *.supabase.co */}
                        <Route path="/auth/confirm" element={<React.Suspense fallback={<LazyFallback />}><ConfirmEmailPage /></React.Suspense>} />
                        <Route path="/accept-invite" element={<React.Suspense fallback={<LazyFallback />}><AcceptInvitePage /></React.Suspense>} />

                        {/* Main Application Routes */}
                        <Route path="/app/*" element={
                          <ProtectedRoute>
                            <UserDashboardWrapper>
                              <React.Suspense fallback={<LazyFallback />}>
                                <Routes>
                                  <Route path="/" element={<MainLayout />}>
                                    {/* Rotas que exigem assinatura ativa ou trial (TrialGuard redireciona expirados para /app/billing) */}
                                    <Route element={<TrialGuard />}>
                                      <Route index element={<Dashboard />} />
                                      <Route path="questions" element={<QuestionBank />} />
                                      <Route path="editor" element={<AssessmentEditor />} />
                                      <Route path="wizard" element={<AssessmentWizard />} />
                                      <Route path="assessments" element={<MyAssessments />} />
                                      <Route path="templates" element={<Templates />} />
                                      <Route path="banco-avaliacoes" element={<AssessmentBank />} />
                                      <Route path="analytics" element={<Analytics />} />
                                      <Route path="collaboration" element={<Collaboration />} />
                                      <Route path="reports" element={<Reports />} />
                                    </Route>
                                    {/* Rotas isentas: usuário expirado precisa delas para assinar/gerenciar a conta */}
                                    <Route path="settings" element={<Settings />} />
                                    <Route path="billing" element={<Billing />} />
                                    <Route path="indicar" element={<ReferralPage />} />
                                    <Route path="subscription-management" element={<SubscriptionManagement />} />
                                    <Route path="help" element={<HelpCenter />} />
                                    <Route path="escola" element={
                                      <SchoolMemberRoute>
                                        <SchoolArea />
                                      </SchoolMemberRoute>
                                    } />
                                    {/* Nome antigo da rota: apontava para a tela do admin da plataforma */}
                                    <Route path="school-admin" element={<Navigate to="/app/escola" replace />} />
                                  </Route>
                                </Routes>
                              </React.Suspense>
                            </UserDashboardWrapper>
                          </ProtectedRoute>
                        } />

                        {/* Admin Panel Routes */}
                        <Route path="/admin/*" element={
                          <ProtectedRoute>
                            <AdminProtectedRoute>
                              <React.Suspense fallback={<LazyFallbackInline />}>
                                <Routes>
                                  <Route path="/" element={<AdminLayout />}>
                                    <Route index element={<AdminOverview />} />
                                    <Route path="users" element={<UserManagement />} />
                                    <Route path="questions" element={<QuestionManagement />} />
                                    <Route path="feedback" element={<UnifiedFeedbackManagement />} />
                                    <Route path="feedback/dashboard" element={<UnifiedFeedbackManagement />} />
                                    <Route path="feedback/report" element={<UnifiedFeedbackManagement />} />
                                    <Route path="feedback/audit" element={<UnifiedFeedbackManagement />} />
                                    <Route path="bulk-generator" element={<BulkQuestionGenerator />} />
                                    <Route path="bulk-review" element={<BulkQuestionReview />} />
                                    <Route path="bulk-jobs" element={<BulkGenerationJobManager />} />
                                    <Route path="generation-batches" element={<GenerationBatchManager />} />
                                    <Route path="auto-generation" element={<AutoGenerationManager />} />
                                    <Route path="auto-generation/:planId" element={<AutoGenerationManager />} />
                                    <Route path="assessments" element={<AssessmentManagement />} />
                                    <Route path="public-assessments" element={<PublicAssessmentManagement />} />
                                    <Route path="templates" element={<TemplateManagement />} />
                                    <Route path="subscriptions" element={<SubscriptionOverview />} />
                                    <Route path="notifications" element={<NotificationManagement />} />
                                    <Route path="analytics" element={<AdminAnalytics />} />
                                    <Route path="seo" element={<SEOManagement />} />
                                    <Route path="settings" element={<SystemSettings />} />
                                    <Route path="security" element={<SecurityDashboard />} />
                                    <Route path="ai-providers" element={<AIProviderSettings />} />
                                    <Route path="plans" element={<PlanManagement />} />
                                    <Route path="schools" element={<SchoolManagement />} />
                                    <Route path="trials" element={<TrialManagement />} />
                                    <Route path="campanhas" element={<SeasonalCampaignManagement />} />
                                    <Route path="campanhas/nova" element={<SeasonalCampaignForm />} />
                                    <Route path="campanhas/:id/editar" element={<SeasonalCampaignForm />} />
                                    <Route path="campanhas/:id/analytics" element={<SeasonalCampaignAnalytics />} />
                                    <Route path="campanhas/leads" element={<SeasonalLeadManagement />} />
                                    <Route path="planos-personalizados" element={<CustomPlanRequestsManagement />} />
                                    {/* Blog Admin Routes */}
                                    <Route path="blog" element={<BlogDashboard />} />
                                    <Route path="blog/novo" element={<BlogPostEditor />} />
                                    <Route path="blog/editar/:id" element={<BlogPostEditor />} />
                                    <Route path="blog/categorias" element={<BlogCategoryManager />} />
                                    <Route path="blog/tags" element={<BlogTagManager />} />
                                    <Route path="blog/auto-generation" element={<BlogAutoGeneration />} />
                                    <Route path="blog/calendario" element={<BlogContentCalendar />} />
                                    <Route path="blog/datas-comemorativas" element={<BlogCommemorativeDates />} />
                                    <Route path="blog/newsletter" element={<NewsletterManagement />} />
                                  </Route>
                                </Routes>
                              </React.Suspense>
                            </AdminProtectedRoute>
                          </ProtectedRoute>
                        } />

                        {/* Catch-all route for 404 */}
                        <Route path="*" element={<React.Suspense fallback={<LazyFallback />}><NotFoundPage /></React.Suspense>} />
                      </Routes>
                    </div>
                  </QuestionSelectionProvider>
                </ThemeProvider>
              </SubscriptionProvider>
            </AuthProvider>
            </SessionManagerWrapper>
          </QueryClientProvider>
)

// Data router com UMA rota catch-all. Motivo: `useBlocker` (guard de saída do
// editor em links internos e no voltar do navegador) só existe em data
// routers. As árvores <Routes> descendentes continuam iguais — casam por
// pathname, apenas não participam de loaders/actions. O ErrorBoundary vai
// DENTRO do elemento da rota para que erros de render continuem caindo na
// nossa tela, e não no errorElement padrão do React Router.
const router = createBrowserRouter(
  [
    {
      path: '*',
      element: (
        <ErrorBoundary>
          <AppShell />
        </ErrorBoundary>
      )
    }
  ],
  { future: { v7_relativeSplatPath: true } }
)

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </HelmetProvider>

      {/* Debug Trigger - Only in development */}
      <DebugTrigger />
    </ErrorBoundary>
  )
}

export default App